import React from "react";
import { Alert, Box, Button, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { ReportFilters } from "./Filters/ReportFilters";
import ReportsLayout from "./ReportsLayout";
import { ReportHeader } from "./ReportShell/ReportHeader";
import { SpecimenDrawer } from "./Specimens/SpecimenDrawer";
import { Segmented } from "./Charts/Segmented";
import { TemplatePreview } from "./Templates/TemplatePreview";
import { HowCalculated } from "./ReportShell/HowCalculated";
import { DataDictionary } from "./Dictionary/DataDictionary";
import { UnfilteredWarning } from "./ReportShell/UnfilteredWarning";

// The page every report shares: header, filters, the report itself.
export const Reports = ({
  definition,
  subtitle,
  popout,
  filtersOpen,
  setFiltersOpen,
  filters,
  onFiltersChange,
  activeFilterCount,
  onResetFilters,
  onBack,
  onSwitchReport,
  onPopout,
  timeZone,
  loading,
  handleGenerateReport,
  unfilteredWarning,
  isChart,
  pivotModel,
  weekPicker,
  chartView,
  onCellClick,
  onRowClick,
  specimens,
  siteGrouping,
  overlapNote,
  template,
  onOpenDictionary,
  onDownloadDictionary,
  timeChart,
  transposed,
  onToggleTranspose,
  readyDownload,
  handleDownloadClicked,
  handleDownloadChartData,
  rawData,
  onDownloadData,
  columnVisibilityModel,
  setColumnVisibilityModel,
  rawColumns,
  reportFields,
  fieldsFailed,
  onRetryFields,
  showReport,
  dataGridRef,
  registerChartExport,
}) => {
  return (
    <>
      {/* minWidth: 0 lets this column shrink to the space beside the nav rail.
          A flex item otherwise never gets narrower than its content, and a data
          grid's content is all of its columns side by side - thousands of
          pixels for Raw data. */}
      <Box
        data-testid="report-shell"
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          bgcolor: "background.paper",
        }}
      >
        <ReportHeader
          definition={definition}
          subtitle={subtitle}
          popout={popout}
          onBack={onBack}
          onSwitch={onSwitchReport}
          onPopout={onPopout}
        >
          {onToggleTranspose && (
            <Tooltip title="Swap rows and columns">
              <IconButton
                aria-label="Swap rows and columns"
                aria-pressed={transposed}
                onClick={onToggleTranspose}
                color={transposed ? "primary" : "default"}
                sx={transposed ? { bgcolor: "action.selected" } : undefined}
              >
                <SwapHorizIcon />
              </IconButton>
            </Tooltip>
          )}
          {definition.kind !== "dictionary" && (
            <HowCalculated definition={definition} fields={reportFields ?? []} onOpenDictionary={onOpenDictionary} />
          )}
          {definition.kind === "dictionary" ? (
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={!reportFields?.length} onClick={onDownloadDictionary}>
              Download
            </Button>
          ) : template ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!readyDownload}
              onClick={() => template.onDownload()}
            >
              {template.exporting ? "Preparing…" : "Download"}
            </Button>
          ) : isChart ? (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!readyDownload}
                onClick={() => handleDownloadClicked()}
              >
                Download image
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!readyDownload}
                onClick={() => handleDownloadChartData()}
              >
                Download data
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!readyDownload}
              onClick={() => handleDownloadClicked()}
            >
              Download
            </Button>
          )}
        </ReportHeader>

        {definition.kind !== "dictionary" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: { xs: 2, md: 3 },
            pb: 1.5,
          }}
        >
          <Button
            size="small"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-expanded={filtersOpen}
            aria-label={`${filtersOpen ? "Hide filters" : "Show filters"}${
              activeFilterCount ? `, ${activeFilterCount} set` : ""
            }`}
            aria-controls="report-filters"
            startIcon={<FilterListIcon />}
            endIcon={
              <ArrowDropDownIcon
                sx={{ transform: filtersOpen ? "rotate(180deg)" : "none", transition: "transform 150ms" }}
              />
            }
          >
            {filtersOpen ? "Hide filters" : "Show filters"}
            {activeFilterCount > 0 && (
              <Box
                component="span"
                aria-hidden
                sx={{
                  ml: 1,
                  px: 0.75,
                  minWidth: 20,
                  borderRadius: 10,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontSize: "0.75rem",
                  lineHeight: "20px",
                  textAlign: "center",
                }}
              >
                {activeFilterCount}
              </Box>
            )}
          </Button>
          <Button size="small" onClick={onResetFilters} disabled={!activeFilterCount} id="reset-data-download-filters">
            Reset
          </Button>
          <Box sx={{ flex: 1 }} />
          {siteGrouping && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Sites by
              </Typography>
              <Segmented label="Sites by" options={siteGrouping.options} value={siteGrouping.value} onChange={siteGrouping.onChange} />
            </Box>
          )}

          <Button variant="contained" size="small" onClick={handleGenerateReport} disableElevation>
            {isChart ? "Generate chart" : template ? "Preview" : "Generate report"}
          </Button>
        </Box>

        )}

        {definition.kind !== "dictionary" && (
          <Box id="report-filters">
            {filtersOpen && <ReportFilters value={filters} onChange={onFiltersChange} fields={reportFields ?? []} />}
          </Box>
        )}

        <Divider />

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {fieldsFailed && (
            <Alert
              severity="error"
              sx={{ mx: { xs: 2, md: 3 }, mt: 2 }}
              action={
                <Button color="inherit" size="small" onClick={onRetryFields}>
                  Retry
                </Button>
              }
            >
              The report fields could not be loaded, so raw data has no columns to show or download and the data
              dictionary is empty.
            </Alert>
          )}
          {overlapNote && (
            <Alert severity="info" sx={{ mx: { xs: 2, md: 3 }, mt: 2 }} data-testid="group-overlap">
              {overlapNote.map((s) => `${s.name} is in ${s.groups.join(" and ")}`).join("; ")}, so{" "}
              {overlapNote.length === 1 ? "its" : "their"} specimens count in each of {overlapNote.length === 1 ? "them" : "their groups"}.
            </Alert>
          )}
          <Box
            id="chart-container"
            sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
          >
            {definition.kind === "dictionary" && <DataDictionary fields={reportFields ?? []} />}
            {showReport && template && (
              <TemplatePreview preview={template.preview} loading={template.loading} title={definition.title} />
            )}
            {showReport && !template && definition.kind !== "dictionary" && (
              <ReportsLayout
                dataGridRef={dataGridRef}
                slug={definition.slug}
                pivotModel={pivotModel}
                weekPicker={weekPicker}
                chartView={chartView}
                onCellClick={onCellClick}
                onRowClick={onRowClick}
                timeChart={timeChart}
                definition={definition}
                rawData={rawData}
                onDownloadData={onDownloadData}
                columnVisibilityModel={columnVisibilityModel}
                rawColumns={rawColumns}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                timeZone={timeZone}
                registerChartExport={registerChartExport}
              />
            )}
          </Box>
        </Box>
        {unfilteredWarning && <UnfilteredWarning {...unfilteredWarning} />}
        {specimens && <SpecimenDrawer browser={specimens.browser} renderEditor={specimens.renderEditor} />}
      </Box>
    </>
  );
};
